import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Battery, CornerDownRight, Maximize2, MessageSquare, Mic, Minimize2, Phone, PhoneOff, Volume2, Wifi } from 'lucide-react';

// Call simulator, ported from the DialFlow prototype. It walks the flow in the browser, without a real call,
// using the same exits as the call engine, so what you test here is what callers get.

/** Day/time in a time zone, to show what Office Hours / Calendar would decide right now. */
function nowIn(timezone) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone || 'Asia/Kolkata', weekday: 'short', hour: '2-digit', minute: '2-digit', year: 'numeric', month: '2-digit', day: '2-digit', hour12: false,
  }).formatToParts(new Date()).map((p) => [p.type, p.value]));
  return { day: parts.weekday.slice(0, 3).toLowerCase(), time: `${parts.hour === '24' ? '00' : parts.hour}:${parts.minute}`, date: `${parts.year}-${parts.month}-${parts.day}` };
}

export function openNow(data) {
  const n = nowIn(data.timezone);
  const days = data.days || ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  return days.includes(n.day) && (data.hours?.start || '09:00') <= n.time && n.time < (data.hours?.end || '18:00');
}

const holidayNow = (data) => (data.holidays || []).includes(nowIn(data.timezone).date);

export default function Simulator({ nodes, edges, team, activeNodeId, setActiveNodeId }) {
  // Starts minimised: inside the dashboard the open phone would cover most of the canvas.
  const [isOpen, setIsOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('+91 98765 43210');
  const [simState, setSimState] = useState('idle');
  const [logs, setLogs] = useState([]);
  const [current, setCurrent] = useState(null);
  const [voicemailText, setVoicemailText] = useState('');
  const [recSeconds, setRecSeconds] = useState(0);
  const timers = useRef([]);
  const logsEnd = useRef(null);
  const audio = useRef(null);
  const live = simState !== 'idle';

  // Pending steps are cleared in place, so hanging up stops anything still scheduled.
  const schedule = (fn, ms) => { timers.current.push(setTimeout(fn, ms)); };
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current.length = 0; };

  useEffect(() => { logsEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [logs]);
  useEffect(() => {
    if (!live) { setRecSeconds(0); return undefined; }
    const t = setInterval(() => setRecSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [live]);
  useEffect(() => {
    const pending = timers.current;
    return () => { pending.forEach(clearTimeout); window.speechSynthesis?.cancel(); };
  }, []);

  const beep = (freq = 800, duration = 300) => {
    try {
      audio.current = audio.current || new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audio.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration / 1000);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + duration / 1000);
    } catch { /* audio blocked by the browser: the transcript still shows everything */ }
  };

  const log = (message, type = 'info') => {
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLogs((prev) => [...prev, { time, message, type }]);
  };

  // Speaks aloud when the browser can; always continues after roughly the time the text takes to say.
  const speak = (text, then) => {
    log(`Voice: "${text}"`, 'speech');
    let done = false;
    const finish = () => { if (!done) { done = true; then(); } };
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.95;
      u.onend = finish;
      u.onerror = finish;
      window.speechSynthesis.speak(u);
    }
    schedule(finish, Math.min(15000, 1200 + text.length * 70));
  };

  const endCall = () => {
    clearTimers();
    window.speechSynthesis?.cancel();
    setSimState('idle');
    setActiveNodeId(null);
    beep(450, 400);
  };

  const advance = (fromId, handle) => {
    const edge = edges.find((e) => e.source === fromId && (e.sourceHandle || 'output') === handle);
    if (edge) {
      setActiveNodeId(edge.target);
    } else {
      log(`Nothing is connected to the "${handle}" exit, so the call ends here.`, 'error');
      setSimState('no_route');
      schedule(endCall, 3000);
    }
  };

  const process = (node) => {
    const d = node.data;
    switch (node.type) {
      case 'start':
        log(`Call answered by "${d.label}"`, 'action');
        schedule(() => advance(node.id, 'output'), 1200);
        break;
      case 'playAudio':
        setSimState('playing_audio');
        speak(d.sourceType === 'url' ? `Playing the audio file ${d.audioUrl || ''}` : (d.message || '...'), () => schedule(() => advance(node.id, 'output'), 600));
        break;
      case 'ivrMenu':
        setSimState('menu_wait');
        speak(d.message || 'Please make a selection.', () => log('Waiting for the caller to press a key...'));
        break;
      case 'officeHours':
        setSimState('hours_wait');
        log(`Checking ${d.label}: right now it is ${openNow(d) ? 'OPEN' : 'CLOSED'} (${d.timezone || 'Asia/Kolkata'})`);
        break;
      case 'calendar':
        setSimState('calendar_wait');
        log(`Checking ${d.label}: today is ${holidayNow(d) ? 'a HOLIDAY' : 'a WORK DAY'}`);
        break;
      case 'team': {
        setSimState('team_wait');
        const names = (d.members || []).map((id) => team.find((t) => t.user_id === id)?.name).filter(Boolean);
        log(`Ringing ${d.queueName || 'the team'}: ${names.join(', ') || 'nobody selected'}`);
        beep(440, 500);
        break;
      }
      case 'callForward':
        setSimState('forward_wait');
        log(`Forwarding to ${d.forwardNumber || '(no number)'}`, 'action');
        if (d.announcement) speak(d.announcement, () => beep(440, 600));
        break;
      case 'voicemail':
        setSimState('voicemail_wait');
        speak(d.message || 'Please leave a message after the tone.', () => { beep(1000, 500); log('Recording... type a message and press Finish.'); });
        break;
      case 'sendSMS':
        setSimState('sms_notify');
        log(`SMS (coming soon, not sent on real calls yet): "${d.smsText || ''}"`, 'action');
        schedule(() => advance(node.id, 'output'), 2500);
        break;
      case 'hangUp':
        setSimState('hung_up');
        log(`Call ended: ${d.reason === 'busy' ? 'busy signal' : d.reason === 'rejected' ? 'rejected' : 'normal hang-up'}`, 'action');
        beep(350, 600);
        schedule(endCall, 3000);
        break;
      default:
        log('Unknown block. Ending the call.', 'error');
        setSimState('no_route');
    }
  };

  useEffect(() => {
    if (!activeNodeId) return;
    const node = nodes.find((n) => n.id === activeNodeId);
    if (node) {
      setCurrent(node);
      process(node);
    }
    // Runs once per block the caller reaches; `process` reads the latest flow on purpose.
  }, [activeNodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCall = () => {
    const start = nodes.find((n) => n.type === 'start');
    clearTimers();
    setLogs([]);
    if (!start) {
      log('Add a "Call Comes In" block first.', 'error');
      return;
    }
    setSimState('dialing');
    log(`Calling from ${phoneNumber}...`);
    beep(440, 200);
    schedule(() => { setSimState('connecting'); setActiveNodeId(null); schedule(() => setActiveNodeId(start.id), 50); }, 1200);
  };

  const choose = (handle, message) => { log(message, 'action'); advance(current.id, handle); };
  const d = current?.data || {};
  const options = current?.type === 'ivrMenu' ? d.options || [] : [];

  return (
    <div className={`simulator-phone-widget ${isOpen ? 'expanded' : 'collapsed'}`}>
      <button type="button" className="btn-toggle-phone" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}<span>{isOpen ? 'Minimize Simulator' : 'Simulate Call'}</span>
      </button>

      {isOpen && (
        <div className="phone-mockup-wrapper">
          <div className="phone-device">
            <div className="phone-status-bar">
              <span>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              {live && <div className="rec-badge-timer"><span className="rec-dot-pulsing">🔴</span><span>LIVE {String(Math.floor(recSeconds / 60)).padStart(2, '0')}:{String(recSeconds % 60).padStart(2, '0')}</span></div>}
              <div className="phone-status-icons"><Wifi size={12} /><Battery size={14} style={{ transform: 'rotate(90deg)' }} /></div>
            </div>
            <div className="phone-notch" />
            <div className="phone-screen-display">
              {simState === 'idle' && (
                <div className="screen-content dialer-view">
                  <div className="dialer-logo"><Phone className="phone-icon-pulse" size={32} /><h5>Call Simulator</h5><p>Test this flow without a real call</p></div>
                  <div className="number-input-field"><input type="text" aria-label="Caller number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} /></div>
                  <button type="button" className="btn-call-trigger" onClick={startCall}><Phone size={20} /> Call This Flow</button>
                </div>
              )}

              {(simState === 'dialing' || simState === 'connecting') && (
                <div className="screen-content active-call-view">
                  <div className="caller-profile"><div className="caller-avatar">?</div><h3>Your company</h3><p className="call-status-animated">{simState === 'dialing' ? 'Dialing...' : 'Connecting...'}</p></div>
                  <button type="button" className="btn-call-hangup" aria-label="Hang up" onClick={endCall}><PhoneOff size={20} /></button>
                </div>
              )}

              {!['idle', 'dialing', 'connecting'].includes(simState) && (
                <div className="screen-content active-call-view compact-call">
                  <div className="caller-banner">
                    <div className="banner-details"><span className="banner-num">{phoneNumber}</span><span className="banner-status">ACTIVE CALL</span></div>
                    <div className="banner-timer"><span className="record-dot" /> Live</div>
                  </div>
                  <div className="simulator-action-center">
                    {simState === 'playing_audio' && (
                      <div className="sim-action-card audio-playing"><Volume2 size={24} className="playing-pulse" /><h6>Listening</h6><p className="audio-subtitle-text">"{d.label}"</p></div>
                    )}
                    {simState === 'menu_wait' && (
                      <div className="sim-action-card keypad-input">
                        <h6>Press a key</h6>
                        <p className="ivr-text-prompt">"{d.message}"</p>
                        <div className="phone-dialpad-grid">
                          {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((key) => {
                            const opt = options.find((o) => o.key === key);
                            return (
                              <button key={key} type="button" className={`dialpad-key ${opt ? 'configured' : 'disabled'}`} disabled={!opt}
                                onClick={() => { beep(697, 100); choose(`key-${key}`, `Caller pressed ${key}`); }}>
                                <span className="key-number">{key}</span>{opt && <span className="key-subtext">{opt.label.substring(0, 7)}</span>}
                              </button>
                            );
                          })}
                        </div>
                        <button type="button" className="btn-choice closed" style={{ marginTop: 8 }} onClick={() => choose('no-input', 'Caller pressed nothing (on real calls the menu repeats twice first)')}>Press nothing</button>
                      </div>
                    )}
                    {simState === 'hours_wait' && (
                      <div className="sim-action-card choice-routing">
                        <h6>Office hours</h6>
                        <p className="choice-description"><strong>{d.label}</strong>: right now {openNow(d) ? 'Open' : 'Closed'}</p>
                        <div className="choice-buttons-row">
                          <button type="button" className="btn-choice open" onClick={() => choose('open', 'Office is open')}>Office Open</button>
                          <button type="button" className="btn-choice closed" onClick={() => choose('closed', 'Office is closed')}>Office Closed</button>
                        </div>
                      </div>
                    )}
                    {simState === 'team_wait' && (
                      <div className="sim-action-card choice-routing">
                        <h6>Ringing the team</h6>
                        <p className="choice-description">Ringing: <strong>{d.queueName || 'Team'}</strong></p>
                        <div className="choice-buttons-row">
                          <button type="button" className="btn-choice open" onClick={() => choose('answered', 'A team member answered')}>Someone Answers</button>
                          <button type="button" className="btn-choice closed" onClick={() => choose('no-answer', 'Nobody answered')}>No Answer</button>
                        </div>
                      </div>
                    )}
                    {simState === 'forward_wait' && (
                      <div className="sim-action-card choice-routing">
                        <h6>Forwarding</h6>
                        <p className="choice-description">To: <strong>{d.forwardNumber || '—'}</strong></p>
                        <div className="choice-buttons-row">
                          <button type="button" className="btn-choice open" onClick={() => choose('answered', 'Forwarded call answered')}>Answered</button>
                          <button type="button" className="btn-choice holiday" onClick={() => choose('unanswered', 'Forwarded call not answered')}>Unanswered</button>
                        </div>
                      </div>
                    )}
                    {simState === 'calendar_wait' && (
                      <div className="sim-action-card choice-routing">
                        <h6>Holiday calendar</h6>
                        <p className="choice-description"><strong>{d.holidayListName || 'Calendar'}</strong>: today is {holidayNow(d) ? 'a holiday' : 'a work day'}</p>
                        <div className="choice-buttons-row">
                          <button type="button" className="btn-choice open" onClick={() => choose('workday', 'Today is a work day')}>Work Day</button>
                          <button type="button" className="btn-choice holiday" onClick={() => choose('holiday', 'Today is a holiday')}>Holiday</button>
                        </div>
                      </div>
                    )}
                    {simState === 'voicemail_wait' && (
                      <div className="sim-action-card voicemail-input">
                        <Mic size={24} className="recording-pulse" /><h6>Record voicemail</h6>
                        <textarea placeholder="Type your voicemail message here..." value={voicemailText} onChange={(e) => setVoicemailText(e.target.value)} rows={3} />
                        <button type="button" className="btn-send-voicemail" disabled={!voicemailText.trim()}
                          onClick={() => { log(`Voicemail recorded: "${voicemailText}"`, 'action'); setVoicemailText(''); advance(current.id, 'output'); }}>Finish & Send</button>
                      </div>
                    )}
                    {simState === 'sms_notify' && (
                      <div className="sms-banner-overlay">
                        <div className="sms-bubble"><div className="sms-bubble-hdr"><MessageSquare size={12} /><span>MESSAGES • COMING SOON</span></div><p className="sms-bubble-body">"{d.smsText}"</p></div>
                      </div>
                    )}
                    {simState === 'hung_up' && (
                      <div className="sim-action-card call-ended-status"><PhoneOff size={24} style={{ color: 'var(--rose-accent)' }} /><h6>Call Ended</h6></div>
                    )}
                    {simState === 'no_route' && (
                      <div className="sim-action-card call-ended-status error-card"><AlertCircle size={24} style={{ color: 'var(--rose-accent)' }} /><h6>Nothing connected</h6><p>On a real call, the caller is disconnected here.</p></div>
                    )}
                  </div>
                  <button type="button" className="btn-call-hangup" aria-label="Hang up" onClick={endCall}><PhoneOff size={20} /></button>
                </div>
              )}
            </div>
            <div className="phone-home-indicator" />
          </div>

          <div className="simulator-system-logs">
            <div className="logs-header"><span>SIMULATOR LIVE CALL TRANSCRIPT</span><button type="button" className="btn-clear-logs" onClick={() => setLogs([])}>Clear</button></div>
            <div className="logs-scroller">
              {logs.map((l, idx) => (
                <div key={idx} className="log-line">
                  <span className="log-time">{l.time}</span><CornerDownRight size={10} className="log-arrow" />
                  <span className={`log-text ${{ speech: 'text-violet', digit: 'text-cyan', action: 'text-emerald', error: 'text-rose' }[l.type] || 'text-white-muted'}`}>{l.message}</span>
                </div>
              ))}
              {logs.length === 0 && <div className="empty-logs-text">The transcript appears here when you start a simulated call.</div>}
              <div ref={logsEnd} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
